import { Form, InputGroup } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useHookFormMask } from "use-mask-input";
import { resolvePath, yearsSinceDate } from '../utils';
import { useEffect, useState } from 'react';
import PlaceholderInput from './PlaceholderInput';
import { useFieldContext } from './FieldContext';

/**
 * @param {Object} props
 * @param {string} props.type
 * @param {boolean} props.required
 * @param {string} props.label
 * @param {string} props.errorLabel
 * @param {boolean} props.hideLabel
 * @param {boolean} props.hideValidation
 * @param {boolean} props.hideValidationMessage
 * @param {string} props.id
 * @param {string} props.absoluteId
 * @param {string} props.mask
 * @param {"text" | "password" | "phone" | "money" | "email" | "integer" | "birthDate" | "url"} props.preset
 * @param {string} props.invalid
 * @param {string} props.className
 * @param {boolean} props.noMargin
 * @param {string} props.placeholder
 * @param {string} props.placeholderText
 * @param {number} props.flex
 * @param {number} props.minWidth
 * @param {number} props.maxWidth
 * @param {boolean} props.multiline
 * @param {(value: string) => number} props.calculateLength
 * @param {boolean} props.showLength
 * @param {number} props.rows
 * @param {number} props.min
 * @param {number} props.max
 * @param {boolean} props.disabled
 * @param {boolean} props.frozen
 */
export default function TextInput({ required = false, type = "text", label, errorLabel, hideLabel, hideValidation, hideValidationMessage, id, absoluteId, mask, preset, invalid, className = "", noMargin, placeholder = "", placeholderText, flex = 1, minWidth, maxWidth, multiline, showLength, calculateLength = v => v.length, rows, min, max, frozen, children, ...rest }) {
    const context = useFieldContext();
    id = absoluteId ?? (context.idPrefix + id);
    rest.disabled ||= context.disabled;

    const { register, unregister, watch, formState: { errors, isLoading, disabled: formDisabled }, custom: { noAnimation, noAsterisk } } = useFormContext();
    const registerMasked = useHookFormMask(register);
    const [showPassword, setShowPassword] = useState(false);
    const error = resolvePath(errors, id);
    rest.showMaskOnHover ??= false;
    rest.disabled ||= formDisabled;

    useEffect(() => () => {
        unregister(id, { keepDefaultValue: true });
    }, []);

    if (preset === "text") {
        rest.pattern = undefined;
        if (required) {
            rest.validate = (value) => typeof value !== "string" || value.trim() !== "";
        }
    } else if (preset === "password") {
        type = showPassword ? "text" : "password";
    } else if (preset === "phone") {
        type = "tel";
        invalid ??= "Invalid phone number.";
        rest.pattern = /^\(\d{3}\) \d{3}-\d{4}$/;
        mask ??= "(999) 999-9999";
        placeholder = "_";
    } else if (preset === "money") {
        mask ??= "$ 9{0,20}";
        rest.rightAlign = false;
        rest.autoUnmask = true;
    } else if (preset === "email") {
        invalid ??= "Invalid email address.";
        rest.pattern = /\S+@\S+/;
    } else if (preset === "integer") {
        type = "number";
        rest.pattern = /^\d*$/;
        if (min && max) {
            invalid ??= `${label} must be a whole number between ${min} and ${max}.`;
        } else if (min) {
            invalid ??= `${label} must be a whole number that is at least ${min}.`;
        } else if (max) {
            invalid ??= `${label} must be a whole number that is at most ${max}.`;
        }
    } else if (preset === "birthDate") {
        type = "date";
        invalid ??= "Invalid date of birth.";
        rest.validate ??= (value) => {
            if (yearsSinceDate(value + "T00:00") > 150) {
                return false;
            }
            if (new Date(value) > new Date()) {
                return false;
            }
        }
    } else if (preset === "url") {
        invalid ??= "Invalid URL.";
        rest.validate = (value) => {
            if (!required && !value) {
                return;
            }
            if (!URL.canParse(value)) {
                return false;
            }
        }
    } else if (required) {
        rest.validate = (value) => typeof value !== "string" || value.trim() !== "";
    }

    if (isLoading && !noAnimation) {
        return <PlaceholderInput {...{ required, label, hideLabel, flex, minWidth, maxWidth, multiline, rows, noMargin, className }}>{children}</PlaceholderInput>;
    }

    return (
        <Form.Group className={`form-group flex flex-col w-full ${className}`} style={{ flex, minWidth: minWidth + "rem", maxWidth: maxWidth + "rem" }}>
            {!hideLabel && <Form.Label className="text-nowrap text-sm font-medium text-neutral-600 mb-2">{label}{required && !noAsterisk && "*"}</Form.Label>}
            <InputGroup hasValidation>
                <div className="input-group">
                    <Form.Control size="lg" style={{ backgroundPositionX: type === "date" ? "right calc(0.375em + 2.1rem)" : preset === "password" && (mask || watch(id)) ? "right calc(0.375em + 1.8rem)" : "" }} className={`px-3 py-2${showLength ? " pb-4" : ""} text-lg rounded-sm border-neutral-300 border-b-neutral-200 ${error ? "[&]:border-red-400" : ""} inset-shadow-sm inset-shadow-neutral-200`} key={id} {...(multiline ? { as: "textarea", rows } : {})} isInvalid={!hideValidation && error} type={type} min={min} max={max} frozen={frozen ? "" : undefined} placeholder={mask ? placeholderText : placeholder} {...(multiline || (preset === "password" && !mask) ? register(id, { ...rest, placeholder, required, min, max }) : registerMasked(id, mask, { ...rest, placeholder, required, min, max }))} />
                    {preset === "password" && (mask || watch(id)) && <span onClick={() => setShowPassword(s => !s)} className='z-10 cursor-pointer px-3 bg-transparent border-none absolute top-[50%] translate-y-[-50%] right-0'><i className={`bi bi-eye-${showPassword ? "slash-" : ""}fill`} /></span>}
                    {showLength && <span className="absolute z-20 text-gray-700 text-sm bottom-0.5 right-5 text-right text-shadow-md pointer-events-none">{calculateLength(watch(id) ?? "")}</span>}
                </div>
                {!(hideValidation || hideValidationMessage) && error && <Form.Control.Feedback type="invalid" className='block mt-0.5 text-sm'>{error?.type !== "required" && invalid ? invalid : `No ${(errorLabel ?? label).toLowerCase()} entered.`}</Form.Control.Feedback>}
            </InputGroup>
            {children}
        </Form.Group>
    );
}